/**
 * ChatService
 * 
 * Service für Chat-Funktionalität mit OpenAI Integration
 * 
 * Verantwortlichkeiten:
 * - Nachrichten an Backend senden
 * - Chat-Verlauf laden
 * - Datenanalyse durchführen
 * - Chart-Generierung
 * - Session-Management
 */

class ChatService {
    constructor() {
        this.supabase = null;
        this.currentSessionId = null;
        this.currentSessionTitle = null;
        this.currentOrganizationId = null;
        this.currentPageKey = 'global';
        this.currentContext = {};
    }

    /**
     * Initialisiert den Service
     */
    async init(supabase, organizationId, options = {}) {
        this.supabase = supabase;
        this.currentOrganizationId = organizationId;
        this.currentPageKey = (options && options.pageKey) ? String(options.pageKey) : 'global';
        
        // Stelle sicher, dass pageKey im Kontext enthalten ist
        if (this.currentPageKey) {
            this.currentContext = {
                ...this.currentContext,
                pageKey: this.currentPageKey
            };
        }
        
        // Erstelle oder lade aktuelle Session
        await this.getOrCreateSession();
    }

    /**
     * Erstellt oder lädt aktuelle Chat-Session
     */
    async getOrCreateSession() {
        if (!this.supabase || !this.currentOrganizationId) {
            throw new Error('ChatService nicht initialisiert');
        }

        const sessionStorageKey = this.getSessionStorageKey();

        // Prüfe ob bereits Session-ID in localStorage
        const savedSessionId = localStorage.getItem(sessionStorageKey);
        
        if (savedSessionId) {
            // Prüfe ob Session noch existiert
            const { data: session, error } = await this.supabase
                .from('chat_sessions')
                .select('id, title')
                .eq('id', savedSessionId)
                .eq('organization_id', this.currentOrganizationId)
                .single();
                
            if (!error && session) {
                this.currentSessionId = savedSessionId;
                this.currentSessionTitle = session.title || null;
                return savedSessionId;
            }
        }

        // Erstelle neue Session
        const { data: { user } } = await this.supabase.auth.getUser();
        if (!user) {
            throw new Error('User nicht authentifiziert');
        }

        const { data: newSession, error: createError } = await this.supabase
            .from('chat_sessions')
            .insert({
                user_id: user.id,
                organization_id: this.currentOrganizationId,
                context: this.currentContext,
                title: null
            })
            .select()
            .single();

        if (createError) {
            throw new Error('Fehler beim Erstellen der Session: ' + createError.message);
        }

        this.currentSessionId = newSession.id;
        this.currentSessionTitle = newSession.title || null;
        localStorage.setItem(sessionStorageKey, newSession.id);
        
        return newSession.id;
    }

    /**
     * Erstellt einen stabilen localStorage-Key für die Session.
     * Nutzt pageKey, um Sessions pro Seite zu trennen.
     */
    getSessionStorageKey() {
        const pageKey = this.currentPageKey || 'global';
        return `chat_session_${this.currentOrganizationId}_${pageKey}`;
    }

    /**
     * Entfernt die aktuelle Session-Referenz (Speicher + LocalStorage)
     */
    clearCurrentSession() {
        this.currentSessionId = null;
        this.currentSessionTitle = null;
        try {
            if (typeof localStorage !== 'undefined') {
                const key = this.getSessionStorageKey();
                if (key) {
                    localStorage.removeItem(key);
                }
            }
        } catch (_) {
            // ignore localStorage errors
        }
    }

    /**
     * Aktualisiert den Kontext (wird von ModelsPage aufgerufen)
     */
    updateContext(context) {
        this.currentContext = {
            ...this.currentContext,
            ...context
        };
        
        // Aktualisiere Session in DB (optional, kann auch lazy sein)
        if (this.currentSessionId) {
            this.supabase
                .from('chat_sessions')
                .update({ context: this.currentContext, updated_at: new Date().toISOString() })
                .eq('id', this.currentSessionId)
                .then(() => {
                    // Silent update - kein Error-Handling nötig
                });
        }
    }

    /**
     * Listet Chat-Sessions für die aktuelle Organisation und Seite.
     */
    async listSessions(limit = 50) {
        if (!this.supabase || !this.currentOrganizationId) {
            throw new Error('ChatService nicht initialisiert');
        }

        const { data: { session } } = await this.supabase.auth.getSession();
        if (!session) {
            throw new Error('Nicht authentifiziert');
        }

        const { data, error } = await this.supabase
            .from('chat_sessions')
            .select('id, title, created_at, updated_at, context')
            .eq('organization_id', this.currentOrganizationId)
            .order('updated_at', { ascending: false })
            .limit(limit);

        if (error) {
            throw new Error('Fehler beim Laden der Sessions: ' + error.message);
        }

        const pageKey = this.currentPageKey || 'global';
        const filtered = (data || []).filter(item => {
            const ctxKey = item && item.context && item.context.pageKey ? item.context.pageKey : 'models';
            return ctxKey === pageKey;
        });

        return filtered;
    }

    /**
     * Erstellt eine neue Session und setzt sie als aktiv.
     */
    async createNewSession(title = null) {
        if (!this.supabase || !this.currentOrganizationId) {
            throw new Error('ChatService nicht initialisiert');
        }

        const { data: { user } } = await this.supabase.auth.getUser();
        if (!user) {
            throw new Error('User nicht authentifiziert');
        }

        const normalizedTitle = this.normalizeSessionTitle(title);
        const { data: newSession, error } = await this.supabase
            .from('chat_sessions')
            .insert({
                user_id: user.id,
                organization_id: this.currentOrganizationId,
                context: this.currentContext,
                title: normalizedTitle
            })
            .select()
            .single();

        if (error) {
            throw new Error('Fehler beim Erstellen der Session: ' + error.message);
        }

        this.currentSessionId = newSession.id;
        this.currentSessionTitle = newSession.title || null;
        localStorage.setItem(this.getSessionStorageKey(), newSession.id);
        return newSession;
    }

    /**
     * Wechselt die aktive Session.
     */
    async switchSession(session) {
        const sessionId = typeof session === 'string' ? session : session?.id;
        if (!sessionId) return;
        this.currentSessionId = sessionId;
        this.currentSessionTitle = (session && session.title) ? session.title : this.currentSessionTitle;
        localStorage.setItem(this.getSessionStorageKey(), sessionId);
    }

    /**
     * Aktualisiert den Session-Titel.
     */
    async updateSessionTitle(sessionId, title) {
        if (!this.supabase || !sessionId) return;
        const normalizedTitle = this.normalizeSessionTitle(title);
        const { error } = await this.supabase
            .from('chat_sessions')
            .update({ title: normalizedTitle })
            .eq('id', sessionId);
        if (!error && sessionId === this.currentSessionId) {
            this.currentSessionTitle = normalizedTitle;
        }
    }

    /**
     * Löscht eine Session und alle zugehörigen Nachrichten.
     */
    async deleteSession(sessionId) {
        if (!this.supabase || !sessionId) return;
        // Lösche zuerst Nachrichten, dann Session
        const { error: deleteMessagesError } = await this.supabase
            .from('chat_messages')
            .delete()
            .eq('session_id', sessionId)
            .eq('organization_id', this.currentOrganizationId);
        if (deleteMessagesError) {
            throw new Error('Fehler beim Löschen der Nachrichten: ' + deleteMessagesError.message);
        }

        const { data: deletedSessions, error: deleteSessionError } = await this.supabase
            .from('chat_sessions')
            .delete()
            .eq('id', sessionId)
            .eq('organization_id', this.currentOrganizationId)
            .select('id');
        if (deleteSessionError) {
            throw new Error('Fehler beim Löschen der Session: ' + deleteSessionError.message);
        }
        if (!deletedSessions || deletedSessions.length === 0) {
            throw new Error('Session konnte nicht gelöscht werden (keine Berechtigung oder nicht gefunden).');
        }
        if (sessionId === this.currentSessionId) {
            this.clearCurrentSession();
        }
    }

    normalizeSessionTitle(title) {
        if (!title) return null;
        const trimmed = String(title).trim();
        if (!trimmed) return null;
        const maxLen = 80;
        return trimmed.length > maxLen ? trimmed.slice(0, maxLen) : trimmed;
    }

    /**
     * Sendet eine Nachricht an den Chatbot
     * @param {string} message - Die Nachricht
     * @param {AbortSignal} signal - Optional: AbortSignal zum Abbrechen der Anfrage
     * @returns {Promise<Object>} Die Assistant-Nachricht
     */
    async sendMessage(message, signal = null) {
        if (!this.supabase || !this.currentSessionId || !this.currentOrganizationId) {
            throw new Error('ChatService nicht initialisiert');
        }

        const { data: { session } } = await this.supabase.auth.getSession();
        if (!session) {
            throw new Error('Nicht authentifiziert');
        }

        try {
            console.log('[Chat] Sende Nachricht an Server...');
            const response = await fetch('/api/chat/message', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`
                },
                body: JSON.stringify({
                    message: message,
                    sessionId: this.currentSessionId,
                    organizationId: this.currentOrganizationId,
                    context: this.currentContext
                }),
                signal: signal // Unterstützt AbortController
            });

            console.log('[Chat] Server hat geantwortet:', response.status, response.ok ? 'OK' : 'Fehler');

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Fehler beim Senden der Nachricht');
            }

            const data = await response.json();
            console.log('[Chat] Antwort erhalten.');
            const msg = data.message || {};
            if (msg.metadata && msg.metadata.identifiedVariable) {
                this.updateContext({ activeVariable: msg.metadata.identifiedVariable });
            }
            if (msg.metadata && msg.metadata.function_calls && msg.metadata.function_calls.length > 0) {
                console.log('[Chat] Function Calls:', msg.metadata.function_calls);
            }

            // Session-Titel bei erster Nachricht setzen (falls leer)
            if (!this.currentSessionTitle) {
                const nextTitle = this.normalizeSessionTitle(message);
                if (nextTitle) {
                    this.updateSessionTitle(this.currentSessionId, nextTitle);
                }
            }
            return msg;
        } catch (error) {
            // Ignoriere Abort-Fehler (vom User abgebrochen)
            if (error.name === 'AbortError') {
                throw new Error('Anfrage wurde abgebrochen');
            }
            console.error('Fehler beim Senden der Nachricht:', error);
            throw error;
        }
    }

    /**
     * Bricht eine laufende Nachricht ab
     * @param {AbortController} abortController - Der AbortController
     */
    cancelMessage(abortController) {
        if (abortController) {
            abortController.abort();
        }
    }

    /**
     * Löscht den gesamten Chat-Verlauf der aktuellen Session (Clean Chat).
     * @returns {Promise<{ success: boolean, deletedCount?: number }>}
     */
    async clearChat() {
        if (!this.supabase || !this.currentSessionId || !this.currentOrganizationId) {
            throw new Error('ChatService nicht initialisiert');
        }

        const { data: { session } } = await this.supabase.auth.getSession();
        if (!session) {
            throw new Error('Nicht authentifiziert');
        }

        const response = await fetch('/api/chat/clear', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify({
                sessionId: this.currentSessionId,
                organizationId: this.currentOrganizationId
            })
        });

        if (!response.ok) {
            const contentType = response.headers.get('Content-Type') || '';
            let errorMessage = 'Fehler beim Löschen des Chat-Verlaufs';
            if (contentType.includes('application/json')) {
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.error || errorMessage;
                } catch (_) { /* ignore */ }
            } else if (response.status === 404) {
                errorMessage = 'Chat leeren ist nicht verfügbar (404). Bitte Server neu starten (npm run dev).';
            }
            throw new Error(errorMessage);
        }

        const contentType = response.headers.get('Content-Type') || '';
        if (!contentType.includes('application/json')) {
            this.updateContext({ activeVariable: null });
            return { success: true, deletedCount: 0 };
        }
        const data = await response.json();
        this.updateContext({ activeVariable: null });
        return data;
    }

    /**
     * Lädt Chat-Verlauf
     */
    async loadHistory(limit = 50) {
        if (!this.supabase || !this.currentSessionId || !this.currentOrganizationId) {
            throw new Error('ChatService nicht initialisiert');
        }

        const { data: { session } } = await this.supabase.auth.getSession();
        if (!session) {
            throw new Error('Nicht authentifiziert');
        }

        try {
            const response = await fetch(`/api/chat/history?sessionId=${this.currentSessionId}&organizationId=${this.currentOrganizationId}&limit=${limit}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${session.access_token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Fehler beim Laden des Chat-Verlaufs');
            }

            const data = await response.json();
            return data.messages || [];
        } catch (error) {
            console.error('Fehler beim Laden des Chat-Verlaufs:', error);
            throw error;
        }
    }

    /**
     * Führt Datenanalyse durch
     */
    async analyzeData(query, filters = {}) {
        if (!this.supabase || !this.currentOrganizationId) {
            throw new Error('ChatService nicht initialisiert');
        }

        const { data: { session } } = await this.supabase.auth.getSession();
        if (!session) {
            throw new Error('Nicht authentifiziert');
        }

        try {
            const response = await fetch('/api/chat/analyze', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`
                },
                body: JSON.stringify({
                    query: query,
                    organizationId: this.currentOrganizationId,
                    modelId: this.currentContext.modelId || null,
                    filters: filters
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Fehler bei der Datenanalyse');
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Fehler bei der Datenanalyse:', error);
            throw error;
        }
    }

    /**
     * Generiert Chart-Konfiguration aus Daten
     */
    generateChartConfig(data, chartType = 'area') {
        // Transformiert Daten für Chart-Komponenten
        // Nutzt ähnliche Logik wie ChartBlock.jsx
        
        if (!data || data.length === 0) {
            return null;
        }

        const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
        
        if (['area', 'composed', 'stacked'].includes(chartType)) {
            // Transformiere für Area/Composed/Stacked Charts
            return {
                type: chartType,
                data: data.map((item, index) => ({
                    name: monthNames[index] || `Monat ${index + 1}`,
                    value: parseFloat(item) || 0
                })),
                config: {
                    title: '',
                    colors: ['#0ea5e9']
                }
            };
        }
        
        if (['pie', 'donut'].includes(chartType)) {
            // Transformiere für Pie/Donut Charts
            return {
                type: chartType,
                data: data.map((item, index) => ({
                    name: monthNames[index] || `Monat ${index + 1}`,
                    value: parseFloat(item) || 0
                })),
                config: {
                    colors: ['#0ea5e9', '#14b8a6', '#f59e0b', '#ef4444', '#8b5cf6']
                }
            };
        }
        
        return null;
    }

    /**
     * Basis-Analyse-Funktionen
     */
    
    /**
     * Berechnet Summe für eine Variable
     */
    async calculateSum(variableName, filters = {}) {
        // TODO: Implementiere Summen-Berechnung
        // Nutzt model_variable_calculations oder model_category_calculations
        return null;
    }

    /**
     * Berechnet Durchschnitt für eine Variable
     */
    async calculateAverage(variableName, filters = {}) {
        // TODO: Implementiere Durchschnitts-Berechnung
        return null;
    }

    /**
     * Holt Trend-Daten für eine Variable
     */
    async getTrend(variableName, months = 12) {
        // TODO: Implementiere Trend-Analyse
        return null;
    }

    /**
     * Vergleicht zwei Perioden
     */
    async comparePeriods(variableName, period1, period2) {
        // TODO: Implementiere Perioden-Vergleich
        return null;
    }
}

// Global export
export { ChatService };
